import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";

const WEEKDAYS = [
  { key: 1, letter: "M", label: "Mo" },
  { key: 2, letter: "D", label: "Di" },
  { key: 3, letter: "M", label: "Mi" },
  { key: 4, letter: "D", label: "Do" },
  { key: 5, letter: "F", label: "Fr" },
  { key: 6, letter: "S", label: "Sa" },
  { key: 7, letter: "S", label: "So" }
];

const DAY_LABEL_BY_KEY = WEEKDAYS.reduce(
  (acc, day) => ({ ...acc, [day.key]: day.label }),
  {}
);

const MONTH_ORDINALS = [
  { value: "first", label: "ersten" },
  { value: "second", label: "zweiten" },
  { value: "third", label: "dritten" },
  { value: "fourth", label: "vierten" },
  { value: "last", label: "letzten" }
];

const ORDINAL_LABEL_BY_VALUE = MONTH_ORDINALS.reduce(
  (acc, item) => ({ ...acc, [item.value]: item.label }),
  {}
);

const ORDINAL_PARSE = {
  ersten: "first",
  zweiten: "second",
  dritten: "third",
  vierten: "fourth",
  letzten: "last"
};

const DEFAULT_CONFIG = {
  frequency: "weekly",
  interval: 1,
  days: [1],
  monthlyMode: "each",
  monthDays: [1],
  monthlyOrdinal: "first",
  monthlyWeekday: 1
};

function normalizeMonthDays(days) {
  const normalized = [...new Set((days || []).map(day => Number(day)))]
    .filter(day => Number.isFinite(day) && day >= 1 && day <= 31)
    .sort((a, b) => a - b);
  return normalized.length > 0 ? normalized : [1];
}

export function parseWencoRhythmusConfig(rhythmusText = "") {
  const text = String(rhythmusText).trim();
  if (!text) {
    return { ...DEFAULT_CONFIG };
  }

  if (/täglich/i.test(text)) {
    const dayMatch = text.match(/alle\s+(\d+)\s+tag/i);
    return {
      ...DEFAULT_CONFIG,
      frequency: "daily",
      interval: dayMatch ? Math.max(1, Number(dayMatch[1])) : 1,
      days: []
    };
  }

  if (/monatlich/i.test(text)) {
    const monthMatch = text.match(/alle\s+(\d+)\s+monat/i);
    const interval = monthMatch ? Math.max(1, Number(monthMatch[1])) : 1;

    const ordinalMatch = text.match(
      /am\s+(ersten|zweiten|dritten|vierten|letzten)\s+(Mo|Di|Mi|Do|Fr|Sa|So)/i
    );
    if (ordinalMatch) {
      const weekday = WEEKDAYS.find(
        day => day.label.toLowerCase() === ordinalMatch[2].toLowerCase()
      );
      return {
        ...DEFAULT_CONFIG,
        frequency: "monthly",
        interval,
        monthlyMode: "on",
        monthDays: [1],
        monthlyOrdinal: ORDINAL_PARSE[ordinalMatch[1].toLowerCase()] || "first",
        monthlyWeekday: weekday?.key || 1
      };
    }

    const amPart = text.match(/am\s+(.+)$/i);
    const monthDays = amPart
      ? [...amPart[1].matchAll(/(\d{1,2})\./g)].map(match => Number(match[1]))
      : [];

    return {
      ...DEFAULT_CONFIG,
      frequency: "monthly",
      interval,
      monthlyMode: "each",
      monthDays: normalizeMonthDays(monthDays),
      monthlyOrdinal: "first",
      monthlyWeekday: 1
    };
  }

  let interval = 1;
  const weekMatch = text.match(/alle\s+(\d+)\s+wochen/i);
  if (weekMatch) {
    interval = Math.max(1, Number(weekMatch[1]));
  }

  const days = WEEKDAYS.map(({ label, key }) =>
    new RegExp(`\\b${label}\\b`, "i").test(text) ? key : null
  ).filter(Boolean);

  return {
    ...DEFAULT_CONFIG,
    frequency: "weekly",
    interval,
    days: days.length > 0 ? days : [1]
  };
}

function formatMonthDays(monthDays) {
  return normalizeMonthDays(monthDays)
    .map(day => `${day}.`)
    .join(", ");
}

export function formatWencoRhythmusFromConfig(config = {}) {
  const {
    frequency = "weekly",
    interval = 1,
    days = [],
    monthlyMode = "each",
    monthDays = [1],
    monthlyOrdinal = "first",
    monthlyWeekday = 1
  } = config;

  const sortedDays = [...days].sort((a, b) => a - b);
  const dayPart = sortedDays
    .map(key => DAY_LABEL_BY_KEY[key])
    .filter(Boolean)
    .join(", ");

  if (frequency === "daily") {
    if (interval > 1) return `Alle ${interval} Tage`;
    return "Täglich";
  }

  if (frequency === "monthly") {
    const intervalPart =
      interval > 1 ? `Alle ${interval} Monate, ` : "Monatlich, ";

    if (monthlyMode === "on") {
      const ordinalLabel = ORDINAL_LABEL_BY_VALUE[monthlyOrdinal] || "ersten";
      const weekdayLabel = DAY_LABEL_BY_KEY[monthlyWeekday] || "Mo";
      return `${intervalPart}am ${ordinalLabel} ${weekdayLabel}`;
    }

    const daysLabel = formatMonthDays(monthDays);
    return `${intervalPart}am ${daysLabel}`;
  }

  if (interval === 1) {
    return dayPart ? `Wöchentlich, ${dayPart}` : "Wöchentlich";
  }
  return dayPart ? `Alle ${interval} Wochen, ${dayPart}` : `Alle ${interval} Wochen`;
}

const WencoRhythmusConfigOverlay = ({ open, initialConfig, onCancel, onApply }) => {
  const [frequency, setFrequency] = useState("weekly");
  const [interval, setInterval] = useState(1);
  const [selectedDays, setSelectedDays] = useState([1]);
  const [monthlyMode, setMonthlyMode] = useState("each");
  const [monthDays, setMonthDays] = useState([1]);
  const [monthlyOrdinal, setMonthlyOrdinal] = useState("first");
  const [monthlyWeekday, setMonthlyWeekday] = useState(1);

  useEffect(() => {
    if (!open) return;
    const config = { ...DEFAULT_CONFIG, ...initialConfig };
    setFrequency(config.frequency || "weekly");
    setInterval(config.interval || 1);
    setSelectedDays(
      Array.isArray(config.days) && config.days.length > 0 ? config.days : [1]
    );
    setMonthlyMode(config.monthlyMode || "each");
    setMonthDays(normalizeMonthDays(config.monthDays));
    setMonthlyOrdinal(config.monthlyOrdinal || "first");
    setMonthlyWeekday(config.monthlyWeekday || 1);
  }, [open, initialConfig]);

  const resultText = useMemo(
    () =>
      formatWencoRhythmusFromConfig({
        frequency,
        interval,
        days: selectedDays,
        monthlyMode,
        monthDays,
        monthlyOrdinal,
        monthlyWeekday
      }),
    [
      frequency,
      interval,
      selectedDays,
      monthlyMode,
      monthDays,
      monthlyOrdinal,
      monthlyWeekday
    ]
  );

  const toggleDay = dayKey => {
    setSelectedDays(prev => {
      if (prev.includes(dayKey)) {
        if (prev.length === 1) return prev;
        return prev.filter(key => key !== dayKey);
      }
      return [...prev, dayKey].sort((a, b) => a - b);
    });
  };

  const toggleMonthDay = day => {
    setMonthlyMode("each");
    setMonthDays(prev => {
      if (prev.includes(day)) {
        if (prev.length === 1) return prev;
        return prev.filter(value => value !== day);
      }
      return [...prev, day].sort((a, b) => a - b);
    });
  };

  const handleApply = () => {
    onApply({
      frequency,
      interval: Math.max(1, Number(interval) || 1),
      days: frequency === "weekly" ? selectedDays : [],
      monthlyMode,
      monthDays: normalizeMonthDays(monthDays),
      monthlyOrdinal,
      monthlyWeekday
    });
  };

  if (!open) return null;

  const intervalUnit =
    frequency === "daily"
      ? interval === 1
        ? "Tag"
        : "Tage"
      : frequency === "monthly"
      ? interval === 1
        ? "Monat"
        : "Monate"
      : interval === 1
      ? "Woche"
      : "Wochen";

  return (
    <Backdrop
      role="presentation"
      onClick={onCancel}
      onKeyDown={event => {
        if (event.key === "Escape") onCancel();
      }}
    >
      <Dialog
        role="dialog"
        aria-labelledby="wenco-rhythmus-config-title"
        aria-modal="true"
        onClick={event => event.stopPropagation()}
      >
        <Title id="wenco-rhythmus-config-title">Wiederholung konfigurieren</Title>

        <Section>
          <SectionLabel>Häufigkeit</SectionLabel>
          <FrequencyRow>
            {[
              { id: "daily", label: "Täglich" },
              { id: "weekly", label: "Wöchentlich" },
              { id: "monthly", label: "Monatlich" }
            ].map(option => (
              <FrequencyButton
                key={option.id}
                type="button"
                $active={frequency === option.id}
                onClick={() => setFrequency(option.id)}
              >
                {option.label}
              </FrequencyButton>
            ))}
          </FrequencyRow>
          <IntervalRow>
            <span>Alle</span>
            <IntervalInput
              type="number"
              min={1}
              max={99}
              value={interval}
              onChange={event =>
                setInterval(Math.max(1, Number(event.target.value) || 1))
              }
            />
            <span>{intervalUnit}</span>
          </IntervalRow>
        </Section>

        {frequency === "weekly" ? (
          <Section>
            <SectionLabel>Wiederholen am</SectionLabel>
            <WeekdayRow>
              {WEEKDAYS.map(day => (
                <WeekdayButton
                  key={day.key}
                  type="button"
                  $active={selectedDays.includes(day.key)}
                  aria-label={day.label}
                  aria-pressed={selectedDays.includes(day.key)}
                  onClick={() => toggleDay(day.key)}
                >
                  {day.letter}
                </WeekdayButton>
              ))}
            </WeekdayRow>
          </Section>
        ) : null}

        {frequency === "monthly" ? (
          <Section>
            <SectionLabel>Wiederholen</SectionLabel>
            <MonthlyOption>
              <RadioRow>
                <RadioInput
                  type="radio"
                  name="monthly-mode"
                  checked={monthlyMode === "each"}
                  onChange={() => setMonthlyMode("each")}
                />
                <RadioLabel>Jeden</RadioLabel>
              </RadioRow>
              <MonthDayGrid $disabled={monthlyMode !== "each"}>
                {Array.from({ length: 31 }, (_, index) => index + 1).map(day => (
                  <MonthDayButton
                    key={day}
                    type="button"
                    $active={monthDays.includes(day)}
                    $disabled={monthlyMode !== "each"}
                    disabled={monthlyMode !== "each"}
                    onClick={() => toggleMonthDay(day)}
                  >
                    {day}
                  </MonthDayButton>
                ))}
              </MonthDayGrid>
            </MonthlyOption>

            <MonthlyOption>
              <RadioRow>
                <RadioInput
                  type="radio"
                  name="monthly-mode"
                  checked={monthlyMode === "on"}
                  onChange={() => setMonthlyMode("on")}
                />
                <RadioLabel>Am</RadioLabel>
              </RadioRow>
              <OrdinalRow $disabled={monthlyMode !== "on"}>
                <OrdinalSelect
                  value={monthlyOrdinal}
                  disabled={monthlyMode !== "on"}
                  onChange={event => {
                    setMonthlyMode("on");
                    setMonthlyOrdinal(event.target.value);
                  }}
                >
                  {MONTH_ORDINALS.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </OrdinalSelect>
                <OrdinalSelect
                  value={monthlyWeekday}
                  disabled={monthlyMode !== "on"}
                  onChange={event => {
                    setMonthlyMode("on");
                    setMonthlyWeekday(Number(event.target.value));
                  }}
                >
                  {WEEKDAYS.map(day => (
                    <option key={day.key} value={day.key}>
                      {day.label}
                    </option>
                  ))}
                </OrdinalSelect>
              </OrdinalRow>
            </MonthlyOption>
          </Section>
        ) : null}

        <ResultBox>
          Ergebnis: <strong>{resultText}</strong>
        </ResultBox>

        <Actions>
          <ActionButton type="button" onClick={onCancel}>
            Abbrechen
          </ActionButton>
          <ActionButton type="button" $primary onClick={handleApply}>
            Übernehmen
          </ActionButton>
        </Actions>
      </Dialog>
    </Backdrop>
  );
};

WencoRhythmusConfigOverlay.propTypes = {
  open: PropTypes.bool.isRequired,
  initialConfig: PropTypes.shape({
    frequency: PropTypes.string,
    interval: PropTypes.number,
    days: PropTypes.arrayOf(PropTypes.number),
    monthlyMode: PropTypes.string,
    monthDays: PropTypes.arrayOf(PropTypes.number),
    monthlyOrdinal: PropTypes.string,
    monthlyWeekday: PropTypes.number
  }),
  onCancel: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired
};

WencoRhythmusConfigOverlay.defaultProps = {
  initialConfig: null
};

export default WencoRhythmusConfigOverlay;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  background: rgba(26, 26, 26, 0.28);
`;

const Dialog = styled.div`
  width: min(100%, 460px);
  padding: 28px 28px 24px;
  border-radius: 20px;
  background: #fff;
  box-shadow: 0 18px 48px rgba(0, 0, 0, 0.14);
`;

const Title = styled.h2`
  margin: 0 0 24px;
  font-size: 22px;
  line-height: 1.25;
  font-weight: 700;
  color: #1a1a1a;
`;

const Section = styled.section`
  margin-bottom: 22px;
`;

const SectionLabel = styled.div`
  margin-bottom: 10px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #888780;
`;

const FrequencyRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 14px;
`;

const FrequencyButton = styled.button`
  flex: 1;
  min-height: 38px;
  border-radius: 10px;
  border: 1px solid ${({ $active }) => ($active ? "#1a1a1a" : "#e2e0da")};
  background: ${({ $active }) => ($active ? "#1a1a1a" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#888780")};
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
`;

const IntervalRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #888780;
`;

const IntervalInput = styled.input`
  width: 52px;
  height: 34px;
  border: 1px solid #e2e0da;
  border-radius: 8px;
  text-align: center;
  font-size: 14px;
  color: #1a1a1a;
`;

const WeekdayRow = styled.div`
  display: flex;
  gap: 10px;
`;

const WeekdayButton = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid ${({ $active }) => ($active ? "#1a1a1a" : "#e2e0da")};
  background: ${({ $active }) => ($active ? "#1a1a1a" : "#fff")};
  color: ${({ $active }) => ($active ? "#fff" : "#888780")};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

const MonthlyOption = styled.div`
  margin-bottom: 16px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const RadioRow = styled.label`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 10px;
  cursor: pointer;
`;

const RadioInput = styled.input`
  width: 16px;
  height: 16px;
  margin: 0;
  accent-color: #1a1a1a;
  cursor: pointer;
`;

const RadioLabel = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
`;

const MonthDayGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-left: 26px;
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
  pointer-events: ${({ $disabled }) => ($disabled ? "none" : "auto")};
`;

const MonthDayButton = styled.button`
  min-height: 34px;
  border-radius: 8px;
  border: 1px solid
    ${({ $active, $disabled }) =>
      $disabled ? "#ebe9e4" : $active ? "#1a1a1a" : "#e2e0da"};
  background: ${({ $active, $disabled }) =>
    $disabled ? "#fafaf9" : $active ? "#1a1a1a" : "#fff"};
  color: ${({ $active, $disabled }) =>
    $disabled ? "#b4b2a9" : $active ? "#fff" : "#5f5e5a"};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
`;

const OrdinalRow = styled.div`
  display: flex;
  gap: 10px;
  margin-left: 26px;
  opacity: ${({ $disabled }) => ($disabled ? 0.45 : 1)};
`;

const OrdinalSelect = styled.select`
  flex: 1;
  min-height: 38px;
  padding: 0 12px;
  border-radius: 10px;
  border: 1px solid #e2e0da;
  background: #fff;
  font-size: 14px;
  color: #1a1a1a;
  cursor: pointer;

  &:disabled {
    background: #fafaf9;
    color: #b4b2a9;
    cursor: not-allowed;
  }
`;

const ResultBox = styled.div`
  margin-bottom: 22px;
  padding: 14px 16px;
  border-radius: 12px;
  background: #f7f6f2;
  font-size: 14px;
  color: #888780;

  strong {
    color: #1a1a1a;
    font-weight: 700;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: 12px;
`;

const ActionButton = styled.button`
  flex: 1;
  min-height: 44px;
  border-radius: 12px;
  border: 1px solid ${({ $primary }) => ($primary ? "#1a1a1a" : "#e2e0da")};
  background: ${({ $primary }) => ($primary ? "#1a1a1a" : "#fff")};
  color: ${({ $primary }) => ($primary ? "#fff" : "#888780")};
  font-size: 14px;
  font-weight: ${({ $primary }) => ($primary ? 700 : 500)};
  cursor: pointer;
`;
